import { useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { Menu, X, MessageCircle } from "lucide-react";
import { useSettings, whatsappLink } from "@/context/SettingsContext";

const LINKS = [
  { to: "/products", label: "Products" },
  { to: "/capabilities", label: "Capabilities" },
  { to: "/oem-private-label", label: "OEM & Private Label" },
  { to: "/indian-wholesale", label: "Wholesale" },
  { to: "/international-export", label: "Export" },
  { to: "/about", label: "About" },
  { to: "/contact", label: "Contact" },
];

export function Navbar() {
  const [open, setOpen] = useState(false);
  const settings = useSettings();
  const wa = whatsappLink(settings.whatsapp_number, "Hello Shineni India, I have a bulk denim requirement.");

  return (
    <header className="sticky top-0 z-50 border-b border-border bg-white/85 backdrop-blur-xl">
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6">
        <Link to="/" className="flex items-baseline gap-2" data-testid="nav-logo">
          <span className="font-display text-xl font-bold tracking-wide text-indigo">SHINENI</span>
          <span className="font-display text-xs font-semibold tracking-[0.35em] text-gold">INDIA</span>
        </Link>

        <nav className="hidden items-center gap-6 lg:flex" aria-label="Primary">
          {LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              className={({ isActive }) =>
                `text-sm font-medium transition-colors duration-200 hover:text-indigo ${
                  isActive ? "text-indigo underline decoration-gold decoration-2 underline-offset-8" : "text-foreground/70"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden items-center gap-3 lg:flex">
          {wa && (
            <a
              href={wa}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="nav-whatsapp-button"
              aria-label="Chat on WhatsApp"
              className="flex h-9 w-9 items-center justify-center rounded-sm border border-border text-[#1FA855] transition-colors duration-200 hover:bg-[#1FA855] hover:text-white"
            >
              <MessageCircle className="h-4 w-4" />
            </a>
          )}
          <Link
            to="/request-a-quote"
            data-testid="nav-quote-cta"
            className="rounded-sm bg-gold px-5 py-2.5 text-sm font-semibold text-indigo-deep transition-colors duration-200 hover:bg-gold-dark"
          >
            Request a Quote
          </Link>
        </div>

        <button
          className="lg:hidden"
          onClick={() => setOpen(!open)}
          aria-label={open ? "Close menu" : "Open menu"}
          data-testid="nav-mobile-toggle"
        >
          {open ? <X className="h-6 w-6 text-indigo" /> : <Menu className="h-6 w-6 text-indigo" />}
        </button>
      </div>

      {open && (
        <nav className="border-t border-border bg-white px-4 pb-6 pt-2 lg:hidden" aria-label="Mobile" data-testid="nav-mobile-menu">
          {LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              onClick={() => setOpen(false)}
              className="block border-b border-border/60 py-3 text-sm font-medium text-foreground/80"
            >
              {l.label}
            </NavLink>
          ))}
          <div className="mt-4 flex gap-3">
            <Link
              to="/request-a-quote"
              onClick={() => setOpen(false)}
              data-testid="nav-mobile-quote-cta"
              className="flex-1 rounded-sm bg-gold px-4 py-3 text-center text-sm font-semibold text-indigo-deep"
            >
              Request a Quote
            </Link>
            {wa && (
              <a
                href={wa}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center rounded-sm border border-border px-4 text-[#1FA855]"
                aria-label="Chat on WhatsApp"
              >
                <MessageCircle className="h-5 w-5" />
              </a>
            )}
          </div>
        </nav>
      )}
    </header>
  );
}
